import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ShoppingCart, Plus, Trash2, Save, ArrowLeft, Loader2, 
  FileText, Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import DynamicFieldsSection from '../components/custom-fields/DynamicFieldsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POCreateComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prNumber = searchParams.get('pr_number');
  
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // Frappe-style custom fields (admin-defined per DocType)
  const [customData, setCustomData] = useState({});
  const [materialDetails, setMaterialDetails] = useState({}); // Store material details for each item
  
  // Master data
  const [masterData, setMasterData] = useState({
    vendors: [],
    purchasingOrgs: [],
    purchasingGroups: [],
    companyCodes: [],
    plants: [],
    storageLocations: [],
    materials: [],
    materialGroups: [],
    costCenters: [],
    glAccounts: [],
    currencies: ['USD', 'EUR', 'INR', 'GBP', 'JPY', 'CNY'],
    // Vendor assignment data (for filtering)
    vendorPurchasingOrgs: [],
    vendorCompanyCodes: [],
    documentTypes: [
      { key: 'NB', description: 'Standard PO' },
      { key: 'FO', description: 'Framework Order' },
      { key: 'UB', description: 'Stock Transport Order' }
    ],
    paymentTerms: [
      { key: '0001', description: 'Immediately Due Net' },
      { key: '0002', description: 'Within 14 days 2% discount' },
      { key: '0003', description: 'Within 30 days net' },
      { key: 'N30', description: 'Net 30 days' },
      { key: 'N45', description: 'Net 45 days' },
      { key: 'N60', description: 'Net 60 days' }
    ],
    incoterms: [
      { key: 'EXW', description: 'Ex Works' },
      { key: 'FCA', description: 'Free Carrier' },
      { key: 'FOB', description: 'Free on Board' },
      { key: 'CIF', description: 'Cost, Insurance and Freight' },
      { key: 'DDP', description: 'Delivered Duty Paid' },
      { key: 'DAP', description: 'Delivered at Place' }
    ],
    itemCategories: [
      { key: '0', description: 'Standard' },
      { key: 'K', description: 'Consignment' },
      { key: 'L', description: 'Subcontracting' },
      { key: 'S', description: 'Third Party' },
      { key: 'D', description: 'Service' }
    ],
    accountAssignmentCategories: [
      { key: '', description: 'No Account Assignment' },
      { key: 'K', description: 'Cost Center' },
      { key: 'F', description: 'Order' },
      { key: 'A', description: 'Asset' },
      { key: 'P', description: 'Project' }
    ],
    taxCodes: [
      { key: 'V0', description: 'No Tax' },
      { key: 'V1', description: 'Input Tax 5%' },
      { key: 'V2', description: 'Input Tax 10%' },
      { key: 'V3', description: 'Input Tax 18%' }
    ],
    deliveryDateCategories: [
      { key: 'D', description: 'Day' },
      { key: 'W', description: 'Week' },
      { key: 'M', description: 'Month' }
    ],
    stockTypes: [
      { key: '', description: 'Unrestricted' },
      { key: 'Q', description: 'Quality Inspection' },
      { key: 'S', description: 'Blocked Stock' }
    ],
    valuationTypes: [],
    controllingAreas: [],
    profitCenters: [],
    unitsOfMeasure: []
  });

  // Form data
  const [formData, setFormData] = useState({
    document_type: 'NB',
    vendor_id: '',
    company_code: '',
    purchasing_org: '',
    purchasing_group: '',
    document_date: new Date().toISOString().split('T')[0],
    currency: 'USD',
    exchange_rate: '',
    payment_terms: '',
    incoterms: '',
    incoterms_location: '',
    validity_start: '',
    validity_end: '',
    our_reference: '',
    your_reference: '',
    salesperson: '',
    telephone: '',
    header_text: '',
    created_by: 'ADMIN',
    items: [{
      item_number: 10,
      material_id: '',
      short_text: '',
      po_quantity: '',
      unit_of_measure: 'EA',
      net_price: '',
      price_unit: 1,
      currency: 'USD',
      delivery_date: '',
      item_category: '0',
      account_assignment_category: 'K',
      plant: '',
      storage_location: '',
      tax_code: 'V0',
      requisitioner: '',
      tracking_number: '',
      material_group: '',
      acknowledgment_required: false,
      goods_receipt_indicator: true,
      invoice_receipt_indicator: true,
      gr_non_valuated: false,
      gr_based_iv: true,
      cost_center: '',
      gl_account: '',
      controlling_area: '',
      profit_center: '',
      order_price_unit: '',
      delivery_date_category: 'D',
      valuation_type: '',
      stock_type: '',
      closed: false,
      print_price: true,
      vendor_address: '',
      pr_number: '',
      pr_item: null,
      asset_number: '',
      order_number: '',
      wbs_element: ''
    }]
  });

  // eslint-disable-next-line
  useEffect(() => { loadMasterData(); }, []);

  const loadMasterData = async () => {
    try {
      setLoadingData(true);
      const [vendorRes, purchOrgRes, purchGroupRes, companyRes, plantRes, slocRes, materialRes, materialGroupRes, ccRes, glRes, coAreaRes, profitCenterRes, valuationTypesRes, vendorPurchRes, vendorCompRes, docTypesRes, uomRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendor-master/vendors`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-groups`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/company/company-codes`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/storage-locations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-groups`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/controlling/cost-centers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/gl-accounts`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/controlling/controlling-areas`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/controlling/profit-centers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/valuation-types`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendor-purchasing-orgs`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendor-company-codes`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/p2p/document-types/`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/units-of-measure`).catch(() => ({ data: [] }))
      ]);
      
      // Map document types from new API to expected format
      const mappedDocTypes = docTypesRes.data.length > 0 
        ? docTypesRes.data.map(dt => ({ key: dt.document_type_id, description: dt.description, category: dt.category }))
        : [{ key: 'NB', description: 'Standard PO' }, { key: 'FO', description: 'Framework Order' }, { key: 'UB', description: 'Stock Transport Order' }];
      
      setMasterData(prev => ({
        ...prev,
        vendors: vendorRes.data, purchasingOrgs: purchOrgRes.data, purchasingGroups: purchGroupRes.data,
        companyCodes: companyRes.data, plants: plantRes.data, storageLocations: slocRes.data,
        materials: materialRes.data, materialGroups: materialGroupRes.data, costCenters: ccRes.data, glAccounts: glRes.data,
        controllingAreas: coAreaRes.data, profitCenters: profitCenterRes.data, valuationTypes: valuationTypesRes.data,
        vendorPurchasingOrgs: vendorPurchRes.data, vendorCompanyCodes: vendorCompRes.data,
        documentTypes: mappedDocTypes,
        unitsOfMeasure: uomRes.data
      }));
      if (prNumber) await loadPRData(prNumber);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoadingData(false);
    }
  };

  const loadPRData = async (pr_number) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/purchase-requisitions/${pr_number}`);
      const pr = response.data;
      if (pr.status !== 'APPROVED' && pr.status !== 'PARTIALLY_ORDERED') {
        toast.error('PR must be approved before creating PO');
        return;
      }
      setFormData(prev => ({
        ...prev,
        purchasing_org: pr.purchasing_org || '', purchasing_group: pr.purchasing_group || '',
        company_code: pr.company_code || '', vendor_id: pr.items[0]?.desired_vendor || '',
        items: pr.items.map((item, index) => ({
          item_number: (index + 1) * 10, material_id: item.material_id || '', short_text: item.short_text || '',
          po_quantity: item.quantity || '', unit_of_measure: item.unit_of_measure || 'EA',
          net_price: item.estimated_price || item.valuation_price || '', price_unit: 1, currency: item.currency || 'USD',
          delivery_date: item.delivery_date || '', item_category: '0', account_assignment_category: item.account_assignment_category || 'K',
          plant: item.plant || '', storage_location: item.storage_location || '', tax_code: 'V0',
          requisitioner: item.requisitioner || '', material_group: item.material_group || '',
          goods_receipt_indicator: true, invoice_receipt_indicator: true, gr_based_iv: true,
          cost_center: item.cost_center || '', gl_account: item.gl_account || '',
          pr_number: pr.pr_number, pr_item: item.item_number
        }))
      }));
      toast.success(`Loaded PR ${pr_number}`);
    } catch (error) {
      console.error('Error loading PR:', error);
      toast.error('Failed to load PR data');
    }
  };

  // Get vendor-specific filtered data
  const getVendorPurchasingOrgs = () => {
    if (!formData.vendor_id) return [];
    return masterData.vendorPurchasingOrgs.filter(vp => vp.vendor_number === formData.vendor_id);
  };

  const getVendorCompanyCodes = () => {
    if (!formData.vendor_id) return [];
    return masterData.vendorCompanyCodes.filter(vc => vc.vendor_number === formData.vendor_id);
  };

  // Filter dropdowns based on vendor assignments
  const getFilteredPurchasingOrgs = () => {
    if (!formData.vendor_id) return masterData.purchasingOrgs;
    const vendorPurchOrgs = getVendorPurchasingOrgs();
    const assignedOrgIds = vendorPurchOrgs.map(vp => vp.purchasing_org_id);
    return masterData.purchasingOrgs.filter(po => assignedOrgIds.includes(po.purchasing_org_id));
  };

  const getFilteredPurchasingGroups = () => {
    if (!formData.vendor_id) return masterData.purchasingGroups;
    const vendorPurchOrgs = getVendorPurchasingOrgs();
    const assignedGroupIds = vendorPurchOrgs.map(vp => vp.purchasing_group_id);
    return masterData.purchasingGroups.filter(pg => assignedGroupIds.includes(pg.purchasing_group_id));
  };

  const getFilteredCompanyCodes = () => {
    if (!formData.vendor_id) return masterData.companyCodes;
    const vendorCompCodes = getVendorCompanyCodes();
    const assignedCodeIds = vendorCompCodes.map(vc => vc.company_code_id);
    return masterData.companyCodes.filter(cc => assignedCodeIds.includes(cc.company_code_id));
  };

  // Get material-specific filtered plants
  const getFilteredPlantsForItem = (itemIndex) => {
    const md = materialDetails[itemIndex];
    if (!md || !md.plants || md.plants.length === 0) return masterData.plants;
    const assignedPlantIds = md.plants.map(p => p.plant_id);
    return masterData.plants.filter(p => assignedPlantIds.includes(p.plant_id));
  };

  // Get material-specific filtered storage locations (based on selected plant)
  const getFilteredStorageLocationsForItem = (itemIndex) => {
    const currentItem = formData.items[itemIndex];
    const md = materialDetails[itemIndex];
    
    if (!currentItem?.plant) return [];
    
    // If material has plant assignments, filter storage locations from material data
    if (md && md.plants) {
      const plantData = md.plants.find(p => p.plant_id === currentItem.plant);
      if (plantData && plantData.storage_location) {
        // Return only the assigned storage location for this plant
        return masterData.storageLocations.filter(sl => 
          sl.storage_location_id === plantData.storage_location && sl.plant_id === currentItem.plant
        );
      }
    }
    
    // Fallback: filter by plant_id
    return masterData.storageLocations.filter(sl => sl.plant_id === currentItem.plant);
  };

  const handleHeaderChange = async (field, value) => {
    // Clear dependent fields when vendor changes
    if (field === 'vendor_id') {
      setFormData(prev => ({
        ...prev,
        vendor_id: value,
        company_code: '',
        purchasing_org: '',
        purchasing_group: '',
        currency: 'USD',
        payment_terms: '',
        incoterms: ''
      }));
      
      if (value) {
        // Get vendor-specific assignments from already loaded master data
        const vendorPurchasing = masterData.vendorPurchasingOrgs.find(vp => vp.vendor_number === value);
        const vendorCompanyCode = masterData.vendorCompanyCodes.find(vc => vc.vendor_number === value);
        
        if (vendorPurchasing || vendorCompanyCode) {
          setFormData(prev => ({
            ...prev,
            vendor_id: value,
            currency: vendorPurchasing?.order_currency || 'USD',
            purchasing_org: vendorPurchasing?.purchasing_org_id || '',
            purchasing_group: vendorPurchasing?.purchasing_group_id || '',
            incoterms: vendorPurchasing?.incoterms || '',
            payment_terms: vendorPurchasing?.payment_terms || vendorCompanyCode?.payment_terms || '',
            company_code: vendorCompanyCode?.company_code_id || '',
          }));
          toast.success('Vendor data auto-populated. Only assigned values are available in dropdowns.');
        } else {
          toast('No purchasing org or company code assigned to this vendor.', { icon: '⚠️', style: { background: '#FEF3C7', color: '#92400E' } });
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'material_id') {
      // Clear dependent fields when material changes
      newItems[index].plant = '';
      newItems[index].storage_location = '';
      
      if (value) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/materials/${value}/details`);
          const md = response.data;
          
          // Store material details for filtering
          setMaterialDetails(prev => ({ ...prev, [index]: md }));
          
          // Auto-populate fields from material
          if (md.description) newItems[index].short_text = md.description;
          if (md.base_uom) newItems[index].unit_of_measure = md.base_uom;
          if (md.material_group) newItems[index].material_group = md.material_group;
          if (md.valuation_price) newItems[index].net_price = String(md.valuation_price);
          if (md.valuation_currency) newItems[index].currency = md.valuation_currency;
          
          // Auto-select first assigned plant and storage location
          if (md.plants?.length > 0) {
            newItems[index].plant = md.plants[0].plant_id || '';
            newItems[index].storage_location = md.plants[0].storage_location || '';
          }
          
          toast.success('Material data auto-populated. Only assigned plants are available.');
        } catch (error) { 
          console.error('Error loading material:', error);
          setMaterialDetails(prev => ({ ...prev, [index]: null }));
        }
      } else {
        // Clear material details when material is deselected
        setMaterialDetails(prev => ({ ...prev, [index]: null }));
      }
    }
    
    // Clear storage location when plant changes (will be re-filtered)
    if (field === 'plant') {
      newItems[index].storage_location = '';
    }
    
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleItemCheckboxChange = (index, field, checked) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: checked };
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    const nextItemNumber = Math.max(...formData.items.map(item => item.item_number), 0) + 10;
    setFormData(prev => ({ ...prev, items: [...prev.items, {
      item_number: nextItemNumber, material_id: '', short_text: '', po_quantity: '', unit_of_measure: 'EA',
      net_price: '', price_unit: 1, order_price_unit: '', currency: formData.currency || 'USD', 
      delivery_date: '', delivery_date_category: 'D', item_category: '0',
      account_assignment_category: 'K', plant: '', storage_location: '', tax_code: 'V0', requisitioner: '',
      tracking_number: '', material_group: '', acknowledgment_required: false,
      goods_receipt_indicator: true, invoice_receipt_indicator: true, gr_based_iv: true,
      gr_non_valuated: false, valuation_type: '', stock_type: '', closed: false, print_price: true,
      cost_center: '', gl_account: '', controlling_area: '', profit_center: '', vendor_address: '',
      asset_number: '', order_number: '', wbs_element: '', pr_number: ''
    }]}));
    setActiveItemIndex(formData.items.length);
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) { toast.error('At least one item is required'); return; }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: newItems }));
    if (activeItemIndex >= newItems.length) setActiveItemIndex(newItems.length - 1);
  };

  const validateForm = () => {
    if (!formData.vendor_id) { toast.error('Vendor is required'); return false; }
    if (!formData.purchasing_org) { toast.error('Purchasing Organization is required'); return false; }
    if (!formData.purchasing_group) { toast.error('Purchasing Group is required'); return false; }
    if (!formData.company_code) { toast.error('Company Code is required'); return false; }
    
    // Validate vendor assignment
    const validPurchOrgs = getFilteredPurchasingOrgs().map(po => po.purchasing_org_id);
    const validPurchGroups = getFilteredPurchasingGroups().map(pg => pg.purchasing_group_id);
    const validCompanyCodes = getFilteredCompanyCodes().map(cc => cc.company_code_id);
    
    if (formData.vendor_id && !validPurchOrgs.includes(formData.purchasing_org)) {
      toast.error(`Purchasing Org '${formData.purchasing_org}' is not assigned to vendor '${formData.vendor_id}'`);
      return false;
    }
    if (formData.vendor_id && !validPurchGroups.includes(formData.purchasing_group)) {
      toast.error(`Purchasing Group '${formData.purchasing_group}' is not assigned to vendor '${formData.vendor_id}'`);
      return false;
    }
    if (formData.vendor_id && !validCompanyCodes.includes(formData.company_code)) {
      toast.error(`Company Code '${formData.company_code}' is not assigned to vendor '${formData.vendor_id}'`);
      return false;
    }
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      const itemNum = item.item_number;
      if (!item.short_text) { toast.error(`Item ${itemNum}: Short text is required`); return false; }
      if (!item.po_quantity || parseFloat(item.po_quantity) <= 0) { toast.error(`Item ${itemNum}: Valid quantity is required`); return false; }
      if (!item.net_price || parseFloat(item.net_price) < 0) { toast.error(`Item ${itemNum}: Valid net price is required`); return false; }
      if (!item.delivery_date) { toast.error(`Item ${itemNum}: Delivery date is required`); return false; }
      if (!item.plant) { toast.error(`Item ${itemNum}: Plant is required`); return false; }
      
      // Account Assignment Category validations
      if (item.account_assignment_category === 'K') {
        if (!item.cost_center) { toast.error(`Item ${itemNum}: Cost Center is mandatory for Account Assignment 'K'`); return false; }
        if (!item.gl_account) { toast.error(`Item ${itemNum}: G/L Account is mandatory for Account Assignment 'K'`); return false; }
      } else if (item.account_assignment_category === 'A') {
        if (!item.asset_number) { toast.error(`Item ${itemNum}: Asset Number is mandatory for Account Assignment 'A'`); return false; }
        if (!item.gl_account) { toast.error(`Item ${itemNum}: G/L Account is mandatory for Account Assignment 'A'`); return false; }
      } else if (item.account_assignment_category === 'F') {
        if (!item.order_number) { toast.error(`Item ${itemNum}: Order Number is mandatory for Account Assignment 'F'`); return false; }
      } else if (item.account_assignment_category === 'P') {
        if (!item.wbs_element) { toast.error(`Item ${itemNum}: WBS Element is mandatory for Account Assignment 'P'`); return false; }
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      // Clean up payload - convert empty strings to null for optional fields
      const payload = {
        ...formData,
        exchange_rate: formData.exchange_rate ? parseFloat(formData.exchange_rate) : null,
        payment_terms: formData.payment_terms || null,
        incoterms: formData.incoterms || null,
        incoterms_location: formData.incoterms_location || null,
        validity_start: formData.validity_start || null,
        validity_end: formData.validity_end || null,
        our_reference: formData.our_reference || null,
        your_reference: formData.your_reference || null,
        salesperson: formData.salesperson || null,
        telephone: formData.telephone || null,
        header_text: formData.header_text || null,
        items: formData.items.map(item => ({
          ...item,
          po_quantity: parseFloat(item.po_quantity),
          net_price: parseFloat(item.net_price),
          price_unit: parseInt(item.price_unit) || 1,
          material_id: item.material_id || null,
          storage_location: item.storage_location || null,
          material_group: item.material_group || null,
          requisitioner: item.requisitioner || null,
          tracking_number: item.tracking_number || null,
          cost_center: item.cost_center || null,
          gl_account: item.gl_account || null,
          controlling_area: item.controlling_area || null,
          profit_center: item.profit_center || null,
          valuation_type: item.valuation_type || null,
          stock_type: item.stock_type || null,
          vendor_address: item.vendor_address || null,
          order_price_unit: item.order_price_unit || null,
          pr_number: item.pr_number || null,
          asset_number: item.asset_number || null,
          order_number: item.order_number || null,
          wbs_element: item.wbs_element || null
        })),
        custom_data: Object.keys(customData).length > 0 ? customData : null,
      };
      const response = await axios.post(`${BACKEND_URL}/api/purchase-orders`, payload);
      toast.success(`Purchase Order ${response.data.po_number} created successfully!`);
      navigate(`/p2p/po-view/${response.data.po_number}`);
    } catch (error) {
      console.error('Error creating PO:', error);
      // Handle validation error response properly
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors come as array
        const errorMsg = errorDetail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        toast.error(`Validation Error: ${errorMsg}`);
      } else {
        toast.error('Failed to create Purchase Order');
      }
    } finally { setLoading(false); }
  };

  const currentItem = formData.items[activeItemIndex] || formData.items[0];
  const getFilteredStorageLocations = () => currentItem?.plant ? masterData.storageLocations.filter(sl => sl.plant_id === currentItem.plant) : [];

  if (loadingData) {
    return (<Layout><div className="flex items-center justify-center h-screen"><Loader2 className="w-12 h-12 animate-spin text-orange-600" /><span className="ml-3">Loading master data...</span></div></Layout>);
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate('/p2p/po-list')}><ArrowLeft className="w-4 h-4 mr-2" />Back to List</Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ShoppingCart className="w-7 h-7 text-orange-600" />Create Purchase Order</h1>
                {prNumber && <p className="text-sm text-gray-600 mt-1 flex items-center gap-2"><FileText className="w-4 h-4" />From PR: {prNumber}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-1">
            {[
              { key: 'header', label: 'Header' },
              { key: 'item_overview', label: 'Item Overview' },
              { key: 'item_detail', label: 'Item Detail' },
              { key: 'custom_fields', label: 'Custom Fields' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-orange-600 text-orange-600 bg-orange-50' : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'}`}
                data-testid={`po-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* HEADER TAB */}
          {activeTab === 'header' && (
            <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4 text-gray-900">Document Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label>Document Type *</Label>
                    <select value={formData.document_type} onChange={(e) => handleHeaderChange('document_type', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="">Select Document Type</option>
                      {masterData.documentTypes.map((dt, idx) => (<option key={`${dt.key}-${dt.category || idx}`} value={dt.key}>{dt.key} - {dt.description}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label>Document Date *</Label>
                    <Input type="date" value={formData.document_date} onChange={(e) => handleHeaderChange('document_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Vendor *</Label>
                    <select value={formData.vendor_id} onChange={(e) => handleHeaderChange('vendor_id', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required>
                      <option value="">Select Vendor</option>
                      {masterData.vendors.filter(v => v.status === 'Active').map(v => (<option key={v.vendor_number} value={v.vendor_number}>{v.vendor_number} - {v.name}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label>Currency *</Label>
                    <select value={formData.currency} onChange={(e) => handleHeaderChange('currency', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                      {masterData.currencies.map(c => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-md font-semibold mb-3 text-gray-700">Organizational Data</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Company Code *</Label>
                    <select value={formData.company_code} onChange={(e) => handleHeaderChange('company_code', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required disabled={!formData.vendor_id}>
                      <option value="">{formData.vendor_id ? (getFilteredCompanyCodes().length ? 'Select Company Code' : 'No Company Code Assigned') : 'Select Vendor First'}</option>
                      {getFilteredCompanyCodes().map(cc => (<option key={cc.company_code_id} value={cc.company_code_id}>{cc.company_code_id} - {cc.company_code_name}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label>Purchasing Org *</Label>
                    <select value={formData.purchasing_org} onChange={(e) => handleHeaderChange('purchasing_org', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required disabled={!formData.vendor_id}>
                      <option value="">{formData.vendor_id ? (getFilteredPurchasingOrgs().length ? 'Select Purch. Org' : 'No Purch. Org Assigned') : 'Select Vendor First'}</option>
                      {getFilteredPurchasingOrgs().map(po => (<option key={po.purchasing_org_id} value={po.purchasing_org_id}>{po.purchasing_org_id} - {po.name}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label>Purchasing Group *</Label>
                    <select value={formData.purchasing_group} onChange={(e) => handleHeaderChange('purchasing_group', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required disabled={!formData.vendor_id}>
                      <option value="">{formData.vendor_id ? (getFilteredPurchasingGroups().length ? 'Select Purch. Group' : 'No Purch. Group Assigned') : 'Select Vendor First'}</option>
                      {getFilteredPurchasingGroups().map(pg => (<option key={pg.purchasing_group_id} value={pg.purchasing_group_id}>{pg.purchasing_group_id} - {pg.name}</option>))}
                    </select>
                  </div>
                </div>
                {formData.vendor_id && getFilteredCompanyCodes().length === 0 && getFilteredPurchasingOrgs().length === 0 && (
                  <div className="mt-2 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                    <p className="text-sm text-yellow-800">⚠️ <strong>Warning:</strong> This vendor has no Purchasing Org or Company Code assignments. Please assign organizational data in Vendor Master before creating a PO.</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-md font-semibold mb-3 text-gray-700">Terms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label>Payment Terms</Label>
                    <select value={formData.payment_terms} onChange={(e) => handleHeaderChange('payment_terms', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="">Select</option>
                      {masterData.paymentTerms.map(pt => (<option key={pt.key} value={pt.key}>{pt.key} - {pt.description}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label>Incoterms</Label>
                    <select value={formData.incoterms} onChange={(e) => handleHeaderChange('incoterms', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="">Select</option>
                      {masterData.incoterms.map(ic => (<option key={ic.key} value={ic.key}>{ic.key} - {ic.description}</option>))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Incoterms Location</Label>
                    <Input value={formData.incoterms_location} onChange={(e) => handleHeaderChange('incoterms_location', e.target.value)} placeholder="Location" maxLength={70} />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-md font-semibold mb-3 text-gray-700">Validity & References</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div><Label>Validity Start</Label><Input type="date" value={formData.validity_start} onChange={(e) => handleHeaderChange('validity_start', e.target.value)} /></div>
                  <div><Label>Validity End</Label><Input type="date" value={formData.validity_end} onChange={(e) => handleHeaderChange('validity_end', e.target.value)} /></div>
                  <div><Label>Our Reference</Label><Input value={formData.our_reference} onChange={(e) => handleHeaderChange('our_reference', e.target.value)} maxLength={12} /></div>
                  <div><Label>Your Reference</Label><Input value={formData.your_reference} onChange={(e) => handleHeaderChange('your_reference', e.target.value)} maxLength={12} /></div>
                </div>
              </div>

              <div>
                <h3 className="text-md font-semibold mb-3 text-gray-700">Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label>Salesperson</Label><Input value={formData.salesperson} onChange={(e) => handleHeaderChange('salesperson', e.target.value)} maxLength={30} /></div>
                  <div><Label>Telephone</Label><Input value={formData.telephone} onChange={(e) => handleHeaderChange('telephone', e.target.value)} maxLength={16} /></div>
                  <div className="md:col-span-1"><Label>Header Text</Label><textarea value={formData.header_text} onChange={(e) => handleHeaderChange('header_text', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" rows={2} maxLength={500} placeholder="Notes..." /></div>
                </div>
              </div>
            </div>
          )}

          {/* ITEM OVERVIEW TAB */}
          {activeTab === 'item_overview' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <div><h2 className="text-lg font-semibold text-gray-900">PO Line Items</h2><p className="text-sm text-gray-500">Item overview and basic information</p></div>
                <Button type="button" onClick={addItem} size="sm"><Plus className="w-4 h-4 mr-2" />Add Item</Button>
              </div>

              {/* Item Selector - Button Style like PR */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {formData.items.map((item, index) => (
                  <Button key={index} type="button" variant={activeItemIndex === index ? "default" : "outline"} size="sm" onClick={() => setActiveItemIndex(index)} className="relative">
                    Item {item.item_number}
                    {formData.items.length > 1 && (
                      <Trash2 className="h-3 w-3 ml-2 cursor-pointer hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeItem(index); }} />
                    )}
                  </Button>
                ))}
              </div>

              {currentItem && (
                <div className="border-t pt-4">
                  <div className="bg-orange-50 p-3 rounded-md flex items-start gap-2 mb-4">
                    <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-orange-800">Editing Item {currentItem.item_number}. Switch to &quot;Item Detail&quot; tab for location and indicator settings.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div><Label>Item Number</Label><Input value={currentItem.item_number} disabled className="bg-gray-100" /></div>
                    <div><Label>Item Category</Label>
                      <select value={currentItem.item_category} onChange={(e) => handleItemChange(activeItemIndex, 'item_category', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        {masterData.itemCategories.map(ic => (<option key={ic.key} value={ic.key}>{ic.key} - {ic.description}</option>))}
                      </select>
                    </div>
                    <div><Label>Material</Label>
                      <select value={currentItem.material_id} onChange={(e) => handleItemChange(activeItemIndex, 'material_id', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Select Material</option>
                        {masterData.materials.filter(m => m.status === 'ACTIVE').map(m => (<option key={m.material_number} value={m.material_number}>{m.material_number} - {m.description}</option>))}
                      </select>
                    </div>
                    <div className="md:col-span-2"><Label>Short Text *</Label><Input value={currentItem.short_text} onChange={(e) => handleItemChange(activeItemIndex, 'short_text', e.target.value)} maxLength={40} required /></div>
                    <div><Label>PO Quantity *</Label><Input type="number" step="0.001" value={currentItem.po_quantity} onChange={(e) => handleItemChange(activeItemIndex, 'po_quantity', e.target.value)} required /></div>
                    <div>
                      <Label>UOM *</Label>
                      <select
                        value={currentItem.unit_of_measure}
                        onChange={(e) => handleItemChange(activeItemIndex, 'unit_of_measure', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        required
                      >
                        <option value="">Select UOM</option>
                        {masterData.unitsOfMeasure.map(uom => (
                          <option key={uom.unit_code} value={uom.unit_code}>
                            {uom.unit_code} - {uom.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div><Label>Delivery Date Category</Label>
                      <select value={currentItem.delivery_date_category || 'D'} onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date_category', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        {masterData.deliveryDateCategories.map(dc => (<option key={dc.key} value={dc.key}>{dc.key} - {dc.description}</option>))}
                      </select>
                    </div>
                    <div><Label>Delivery Date *</Label><Input type="date" value={currentItem.delivery_date} onChange={(e) => handleItemChange(activeItemIndex, 'delivery_date', e.target.value)} required /></div>
                    <div><Label>Net Price *</Label><Input type="number" step="0.01" value={currentItem.net_price} onChange={(e) => handleItemChange(activeItemIndex, 'net_price', e.target.value)} required /></div>
                    <div><Label>Currency</Label>
                      <select value={currentItem.currency} onChange={(e) => handleItemChange(activeItemIndex, 'currency', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        {masterData.currencies.map(c => (<option key={c} value={c}>{c}</option>))}
                      </select>
                    </div>
                    <div><Label>Price Unit</Label><Input type="number" value={currentItem.price_unit} onChange={(e) => handleItemChange(activeItemIndex, 'price_unit', e.target.value)} min={1} /></div>
                    <div><Label>Order Price Unit</Label><Input value={currentItem.order_price_unit || ''} onChange={(e) => handleItemChange(activeItemIndex, 'order_price_unit', e.target.value)} maxLength={3} /></div>
                    <div><Label>Material Group</Label>
                      <select value={currentItem.material_group} onChange={(e) => handleItemChange(activeItemIndex, 'material_group', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">Select</option>
                        {masterData.materialGroups.map(mg => (<option key={mg.material_group_id} value={mg.material_group_id}>{mg.material_group_id} - {mg.description}</option>))}
                      </select>
                    </div>
                    <div><Label>Plant *</Label>
                      <select value={currentItem.plant} onChange={(e) => handleItemChange(activeItemIndex, 'plant', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required disabled={!currentItem.material_id}>
                        <option value="">{currentItem.material_id ? (getFilteredPlantsForItem(activeItemIndex).length ? 'Select Plant' : 'No Plant Assigned') : 'Select Material First'}</option>
                        {getFilteredPlantsForItem(activeItemIndex).map(p => (<option key={p.plant_id} value={p.plant_id}>{p.plant_id} - {p.plant_name}</option>))}
                      </select>
                    </div>
                    <div><Label>Storage Location</Label>
                      <select value={currentItem.storage_location} onChange={(e) => handleItemChange(activeItemIndex, 'storage_location', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" disabled={!currentItem.plant}>
                        <option value="">{currentItem.plant ? (getFilteredStorageLocationsForItem(activeItemIndex).length ? 'Select' : 'No Storage Location Assigned') : 'Select Plant First'}</option>
                        {getFilteredStorageLocationsForItem(activeItemIndex).map(sl => (<option key={sl.storage_location_id} value={sl.storage_location_id}>{sl.storage_location_id} - {sl.name}</option>))}
                      </select>
                    </div>
                    <div><Label>Purchase Requisition</Label><Input value={currentItem.pr_number || ''} onChange={(e) => handleItemChange(activeItemIndex, 'pr_number', e.target.value)} placeholder="PR Number" /></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ITEM DETAIL TAB */}
          {activeTab === 'item_detail' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <div><h2 className="text-lg font-semibold text-gray-900">Item Detail</h2><p className="text-sm text-gray-500">Location, tracking, and indicator settings</p></div>
              </div>

              {/* Item Selector - Button Style */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {formData.items.map((item, index) => (
                  <Button key={index} type="button" variant={activeItemIndex === index ? "default" : "outline"} size="sm" onClick={() => setActiveItemIndex(index)}>
                    Item {item.item_number}
                  </Button>
                ))}
              </div>

              {currentItem && (
                <div className="border-t pt-4 space-y-6">
                  <div className="bg-gray-50 p-3 rounded-md mb-4">
                    <p className="text-sm text-gray-700"><strong>Item {currentItem.item_number}:</strong> {currentItem.short_text || '(No description)'}</p>
                  </div>

                  <div>
                    <h3 className="text-md font-semibold mb-3 text-gray-700">Quantities / Weights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div><Label>Tax Code</Label>
                        <select value={currentItem.tax_code} onChange={(e) => handleItemChange(activeItemIndex, 'tax_code', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                          {masterData.taxCodes.map(tc => (<option key={tc.key} value={tc.key}>{tc.key} - {tc.description}</option>))}
                        </select>
                      </div>
                      <div><Label>Requisitioner</Label><Input value={currentItem.requisitioner} onChange={(e) => handleItemChange(activeItemIndex, 'requisitioner', e.target.value)} maxLength={12} /></div>
                      <div><Label>Tracking Number</Label><Input value={currentItem.tracking_number} onChange={(e) => handleItemChange(activeItemIndex, 'tracking_number', e.target.value)} maxLength={10} /></div>
                      <div className="flex items-center space-x-2 mt-6"><Checkbox id="closed" checked={currentItem.closed} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'closed', c)} /><Label htmlFor="closed" className="text-sm">Closed</Label></div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-md font-semibold mb-3 text-gray-700">Delivery</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div><Label>Valuation Type</Label>
                        <select value={currentItem.valuation_type || ''} onChange={(e) => handleItemChange(activeItemIndex, 'valuation_type', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                          <option value="">Select</option>
                          {masterData.valuationTypes.map(vt => (<option key={vt.valuation_type_id || vt} value={vt.valuation_type_id || vt}>{vt.valuation_type_id || vt}</option>))}
                        </select>
                      </div>
                      <div><Label>Stock Type</Label>
                        <select value={currentItem.stock_type || ''} onChange={(e) => handleItemChange(activeItemIndex, 'stock_type', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                          {masterData.stockTypes.map(st => (<option key={st.key} value={st.key}>{st.key ? `${st.key} - ${st.description}` : st.description}</option>))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-md font-semibold mb-3 text-gray-700">Invoice</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      <div className="flex items-center space-x-2"><Checkbox id="gr" checked={currentItem.goods_receipt_indicator} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'goods_receipt_indicator', c)} /><Label htmlFor="gr" className="text-sm">Goods Receipt</Label></div>
                      <div className="flex items-center space-x-2"><Checkbox id="ir" checked={currentItem.invoice_receipt_indicator} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'invoice_receipt_indicator', c)} /><Label htmlFor="ir" className="text-sm">Invoice Receipt</Label></div>
                      <div className="flex items-center space-x-2"><Checkbox id="grnv" checked={currentItem.gr_non_valuated} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'gr_non_valuated', c)} /><Label htmlFor="grnv" className="text-sm">GR Non-Valuated</Label></div>
                      <div className="flex items-center space-x-2"><Checkbox id="griv" checked={currentItem.gr_based_iv} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'gr_based_iv', c)} /><Label htmlFor="griv" className="text-sm">GR-Based IV</Label></div>
                      <div className="flex items-center space-x-2"><Checkbox id="ack" checked={currentItem.acknowledgment_required} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'acknowledgment_required', c)} /><Label htmlFor="ack" className="text-sm">Acknowledgment Req.</Label></div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-md font-semibold mb-3 text-gray-700">Account Assignment</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div><Label>Account Assignment *</Label>
                        <select value={currentItem.account_assignment_category} onChange={(e) => handleItemChange(activeItemIndex, 'account_assignment_category', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                          {masterData.accountAssignmentCategories.map(aa => (<option key={aa.key} value={aa.key}>{aa.key ? `${aa.key} - ${aa.description}` : aa.description}</option>))}
                        </select>
                      </div>
                      <div><Label>CO Area</Label>
                        <select value={currentItem.controlling_area || ''} onChange={(e) => handleItemChange(activeItemIndex, 'controlling_area', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                          <option value="">Select</option>
                          {masterData.controllingAreas.map(ca => (<option key={ca.controlling_area_id} value={ca.controlling_area_id}>{ca.controlling_area_id} - {ca.name}</option>))}
                        </select>
                      </div>
                      {/* Cost Center - Show if AAC = K */}
                      {currentItem.account_assignment_category === 'K' && (
                        <div><Label>Cost Center *</Label>
                          <select value={currentItem.cost_center} onChange={(e) => handleItemChange(activeItemIndex, 'cost_center', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required>
                            <option value="">Select</option>
                            {masterData.costCenters.map(cc => (<option key={cc.cost_center_id} value={cc.cost_center_id}>{cc.cost_center_id} - {cc.name}</option>))}
                          </select>
                        </div>
                      )}
                      {/* G/L Account - Show if AAC = K or A */}
                      {(currentItem.account_assignment_category === 'K' || currentItem.account_assignment_category === 'A') && (
                        <div><Label>G/L Account *</Label>
                          <select value={currentItem.gl_account} onChange={(e) => handleItemChange(activeItemIndex, 'gl_account', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm" required>
                            <option value="">Select</option>
                            {masterData.glAccounts.map(gl => (<option key={gl.gl_account} value={gl.gl_account}>{gl.gl_account} - {gl.account_name}</option>))}
                          </select>
                        </div>
                      )}
                      {/* Asset Number - Show if AAC = A */}
                      {currentItem.account_assignment_category === 'A' && (
                        <div><Label>Asset Number *</Label>
                          <Input value={currentItem.asset_number || ''} onChange={(e) => handleItemChange(activeItemIndex, 'asset_number', e.target.value)} maxLength={12} placeholder="Asset number" required />
                        </div>
                      )}
                      {/* Order Number - Show if AAC = F */}
                      {currentItem.account_assignment_category === 'F' && (
                        <div><Label>Order Number *</Label>
                          <Input value={currentItem.order_number || ''} onChange={(e) => handleItemChange(activeItemIndex, 'order_number', e.target.value)} maxLength={12} placeholder="Internal order" required />
                        </div>
                      )}
                      {/* WBS Element - Show if AAC = P */}
                      {currentItem.account_assignment_category === 'P' && (
                        <div><Label>WBS Element *</Label>
                          <Input value={currentItem.wbs_element || ''} onChange={(e) => handleItemChange(activeItemIndex, 'wbs_element', e.target.value)} maxLength={24} placeholder="WBS element" required />
                        </div>
                      )}
                      <div><Label>Profit Center</Label>
                        <select value={currentItem.profit_center || ''} onChange={(e) => handleItemChange(activeItemIndex, 'profit_center', e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                          <option value="">Select</option>
                          {masterData.profitCenters.map(pc => (<option key={pc.profit_center_id} value={pc.profit_center_id}>{pc.profit_center_id} - {pc.name}</option>))}
                        </select>
                      </div>
                    </div>
                    {/* Info Box showing mandatory fields based on Account Assignment */}
                    {currentItem.account_assignment_category && currentItem.account_assignment_category !== '' && (
                      <div className="mt-3 bg-orange-50 p-3 rounded-md">
                        <p className="text-sm text-orange-800">
                          <strong>Account Assignment Rules:</strong>{' '}
                          {currentItem.account_assignment_category === 'K' && 'Cost Center assignment requires: Cost Center + G/L Account'}
                          {currentItem.account_assignment_category === 'A' && 'Asset assignment requires: Asset Number + G/L Account'}
                          {currentItem.account_assignment_category === 'F' && 'Order assignment requires: Order Number'}
                          {currentItem.account_assignment_category === 'P' && 'Project assignment requires: WBS Element'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-md font-semibold mb-3 text-gray-700">Delivery Address</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><Label>Vendor Address</Label><Input value={currentItem.vendor_address || ''} onChange={(e) => handleItemChange(activeItemIndex, 'vendor_address', e.target.value)} placeholder="Enter vendor delivery address" /></div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-md font-semibold mb-3 text-gray-700">Condition / Control</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="flex items-center space-x-2"><Checkbox id="print_price" checked={currentItem.print_price !== false} onCheckedChange={(c) => handleItemCheckboxChange(activeItemIndex, 'print_price', c)} /><Label htmlFor="print_price" className="text-sm">Print Price</Label></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CUSTOM FIELDS TAB — Frappe-style low-code */}
          {activeTab === 'custom_fields' && (
            <DynamicFieldsSection
              doctype="purchase_order"
              values={customData}
              onChange={setCustomData}
              title="Purchase Order Custom Fields"
            />
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/p2p/po-list')}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<><Save className="w-4 h-4 mr-2" />Create Purchase Order</>)}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default POCreateComplete;
